import type { ReactNode } from "react";
import { cn } from "./cn";

/**
 * The design has exactly one callout, in the informational blue. The screens
 * being ported also have to report a failed mutation, and the design's other
 * red surfaces (a field's `error`, the danger button) are all too small to
 * carry a sentence of server copy. So the callout takes a tone rather than
 * gaining a second component: same geometry, the palette's own danger/warning
 * ramps, and no new control invented for it.
 */
type Tone = "info" | "danger" | "warning" | "success";

const tones: Record<Tone, { box: string; bar: string; icon: string; title: string }> = {
  info: {
    box: "bg-info-soft ring-brand/10",
    bar: "bg-brand",
    icon: "text-brand",
    title: "text-info-ink",
  },
  danger: {
    box: "bg-danger-soft ring-danger/15",
    bar: "bg-danger",
    icon: "text-danger",
    title: "text-danger-ink",
  },
  warning: {
    box: "bg-warning-soft ring-warning/15",
    bar: "bg-warning",
    icon: "text-warning",
    title: "text-warning-ink",
  },
  success: {
    box: "bg-success-soft ring-success/15",
    bar: "bg-success",
    icon: "text-success",
    title: "text-success-ink",
  },
};

export function Notice({
  title,
  children,
  icon,
  tone = "info",
  className,
}: {
  title?: string;
  children: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  className?: string;
}) {
  const palette = tones[tone];
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-card ring-1",
        palette.box,
        className,
      )}
    >
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-[3px]", palette.bar)} />
      <div className="flex gap-3 py-4 pr-5 pl-5 sm:pl-6">
        {icon ? <span className={cn("mt-0.5 shrink-0", palette.icon)}>{icon}</span> : null}
        <div className="min-w-0">
          {title ? (
            <p className={cn("font-display text-sm font-bold", palette.title)}>{title}</p>
          ) : null}
          <div className={cn("text-xs leading-relaxed text-ink-3", title && "mt-1")}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
